import { BoundaryAssertionType } from '@cadstrom/sigma-engine-shared';

import type { IBoundaryDefinition, IBoundaryDefinitionWithId } from '@cadstrom/sigma-engine-shared';

/**
 * Interface for boundary detail fields used in tooltips and detail displays
 */
export interface BoundaryDetailField {
	label: string;
	value: string | number | null | undefined;
}

/**
 * Helper function to render boundary details based on type for tooltips and detail displays
 */
export function renderBoundaryDetails(boundary: IBoundaryDefinition | undefined): BoundaryDetailField[] {
	const fields: BoundaryDetailField[] = [];
	if (!boundary) {
		return fields;
	}

	if (boundary.type === BoundaryAssertionType.Numerical) {
		fields.push(
			{ label: 'Characteristic', value: boundary.numerical.characteristic.toLowerCase() },
			{ label: 'Type', value: boundary.type },
			{ label: 'Boundary Type', value: boundary.numerical.type },
		);

		switch (boundary.numerical.type) {
			case 'absolute': {
				fields.push({ label: 'Value', value: boundary.numerical.value });
				break;
			}
			case 'range': {
				fields.push(
					{ label: 'Max Value', value: boundary.numerical.maxVal },
					{ label: 'Min Value', value: boundary.numerical.minVal },
				);
				break;
			}
			case 'minimum':
			case 'maximum': {
				fields.push({ label: 'Value', value: boundary.numerical.value });
				break;
			}
			default: {
				break;
			}
		}
	} else if (boundary.type === BoundaryAssertionType.Connection) {
		fields.push({ label: 'Type', value: boundary.type });

		if (boundary.connectionImpedance !== null && boundary.connectionImpedance !== undefined) {
			fields.push({ label: 'Connection Impedance', value: boundary.connectionImpedance });
		}
	}

	return fields;
}

/**
 * Helper function to format boundary value for table display
 */
export function formatBoundaryValueForTable(boundary: IBoundaryDefinition | IBoundaryDefinitionWithId): string {
	if (boundary.type === BoundaryAssertionType.Numerical) {
		const numerical = boundary.numerical;
		const nominal = numerical.nominalValue ?? 'nom';

		switch (numerical.type) {
			case 'minimum': {
				const symbol = numerical.valInclusive === false ? '<' : '≤';
				return `${numerical.value} ${symbol} ${nominal}`;
			}
			case 'maximum': {
				const symbol = numerical.valInclusive === false ? '<' : '≤';
				return `${nominal} ${symbol} ${numerical.value}`;
			}
			case 'range': {
				const maxSymbol = numerical.minValIncluded === false ? '<' : '≤';
				const minSymbol = numerical.maxValIncluded === false ? '<' : '≤';
				return `${numerical.minVal} ${minSymbol} ${nominal} ${maxSymbol} ${numerical.maxVal}`;
			}
			case 'absolute': {
				return 'value' in numerical ? `= ${numerical.value}` : '';
			}
			default: {
				return 'Unknown numerical type';
			}
		}
	} else if (boundary.type === BoundaryAssertionType.Connection) {
		return boundary.connectionImpedance !== null && boundary.connectionImpedance !== undefined ?
				`${boundary.connectionImpedance}Ω`
			:	'Connection';
	}

	return '';
}

/**
 * Helper function to get boundary characteristic for table display
 */
export function getBoundaryCharacteristic(boundary: IBoundaryDefinitionWithId): string {
	if (boundary.type === BoundaryAssertionType.Numerical) {
		return boundary.numerical.characteristic;
	} else if (boundary.type === BoundaryAssertionType.Connection) {
		return 'Connection';
	}
	return '';
}
